import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useDispatch, useSelector } from 'react-redux';
import { createBidApplication, nonEnrolledAuctionsAPI } from '../../../apis';
import { showSnackbar } from '../../../store/slices/common.slice';
import {
  APIResponse,
  Auction,
  FreeKeyObject,
  ListingResponse,
  TypeSnackbarPayload,
} from '../../../utilities/types';
import DatagridLoader from '../../elements/datagrid-loader.element';
import {
  getFormattedDate,
  getFormattedPrice,
  getFormattedQuantity,
  parseIntoNumber,
  prepareAndGetMediaURL,
} from '../../../utilities/helper';
import { RootState } from '../../../store';
import FormModal from '../../modals/form.modal';
import CustomSwal from '../../elements/custom-swal.element';
import BidWidget from '../bid.widget';
import useNavigateHook from '../../../hooks/navigate.hook';

interface NonEnrolledAuctionGridWidgetProps {
  // eslint-disable-next-line react/no-unused-prop-types
  refresh: number;
}

const NonEnrolledAuctionGridWidget: React.FC<NonEnrolledAuctionGridWidgetProps> = () => {
  const dispatch = useDispatch();

  const { doNavigate } = useNavigateHook();

  const { user } = useSelector((state: RootState) => state.auth);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowCount, setRowCount] = useState(0);
  const [showAuctionDetailModal, setShowAuctionDetailModal] = useState<boolean>(false);
  const [selectedAuction, setSelectedAuction] = useState<Auction | null>(null);
  const [isPlacingBid, setIsPlacingBid] = useState<boolean>(false);

  const pageSize = 20;

  // Simulate API call (replace with your real API)
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = (await nonEnrolledAuctionsAPI({
        user_id: user?.id,
      })) as APIResponse<ListingResponse>;
      if (response.status) {
        const items: any = (Array.isArray(response.data) ? response.data : []).map((item: any) => {
          return {
            ...item,
            item_name: item.item?.name,
            quantity: getFormattedQuantity(item.quantity, item.quantity_unit),
            start_time: getFormattedDate(item.start_time),
            end_time: getFormattedDate(item.end_time),
            image: item.item?.image,
          };
        });
        setRows(items);
        setRowCount(items.length);
      }
    } catch (err: any) {
      const snakbarpayload: TypeSnackbarPayload = { severity: 'error', message: err.message };
      if (err?.data?.message) {
        snakbarpayload.message = err?.data?.message;
      } else if (err?.statusText) {
        snakbarpayload.message = err?.statusText;
      }
      dispatch(showSnackbar(snakbarpayload));
    }
    setLoading(false);
  }, [dispatch, user?.id]);

  const applyForBid = useCallback(
    async (auction: Auction) => {
      setIsPlacingBid(true);
      const result = await CustomSwal.fire({
        title: 'Apply for Bid',
        input: 'textarea',
        inputPlaceholder: 'Optional. Type your message here...',
        inputAttributes: {
          maxlength: '200',
        },
        allowOutsideClick: false,
        showCancelButton: true,
      });
      if (result.isConfirmed) {
        try {
          const payload = {
            auction_id: auction?.id,
            buyer_id: user?.id,
            buyer_comment: result.value,
          };
          const response = (await createBidApplication(payload)) as APIResponse<FreeKeyObject>;
          if (response.status) {
            CustomSwal.fire({
              icon: 'success',
              title: 'Bid Request Sent',
              text: `Request has been sent and waiting for approval`,
            });
            fetchData();
          }
        } catch (err: any) {
          const snakbarpayload: TypeSnackbarPayload = { severity: 'error', message: err.message };
          if (err?.data?.message) {
            snakbarpayload.message = err?.data?.message;
          } else if (err?.statusText) {
            snakbarpayload.message = err?.statusText;
          }
          dispatch(showSnackbar(snakbarpayload));
        }
      }
      setIsPlacingBid(false);
    },
    [dispatch, fetchData, user?.id]
  );

  const viewDetail = useCallback((auction: Auction) => {
    doNavigate(`/auction-detail.html?id=${auction.id}`);
    // setSelectedAuction(auction);
    // setShowAuctionDetailModal(true);
  }, [doNavigate]);

  const getBidApplyCTAForAuction = (auction: Auction) => {
    return (
      <Stack gap={2} flexDirection="row">
        <Button
          variant="contained"
          size="small"
          color="info"
          title="Click to view detail"
          disabled={isPlacingBid}
          onClick={() => viewDetail(auction)}
        >
          View
        </Button>
        <Button
          variant="contained"
          size="small"
          color="error"
          title="Click to apply for bid"
          disabled={isPlacingBid}
          onClick={() => applyForBid(auction)}
        >
          Apply
        </Button>
      </Stack>
    );
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Columns Definition
  const columns = [
    {
      field: 'custom_id',
      headerName: 'ID',
      width: 70,
    },
    {
      field: 'title',
      headerName: 'Title',
      width: 150,
      renderCell: (params: any) => (
        <Button
          variant="text"
          size="small"
          color="info"
          title="Click to view full detail"
          onClick={() => viewDetail(params.row)}
        >
          {params.value}
        </Button>
      ),
    },
    {
      field: 'image',
      headerName: 'Image',
      width: 100,
      cellClassName: 'item-img-cell',
      renderCell: (params: any) => (
        <img
          alt="item"
          src={prepareAndGetMediaURL(params.value)}
          style={{ maxWidth: 60, height: 'auto', maxHeight: 60, objectFit: 'cover' }}
        />
      ),
    },
    { field: 'item_name', headerName: 'Item Name', width: 150 },
    { field: 'quantity', headerName: 'Quantity' },
    {
      field: 'end_time',
      headerName: 'Start-End Time',
      sortable: false,
      flex: 1,
      renderCell: (params: any) => {
        return (
          <Stack>
            <Typography variant="body2">
              <strong>Start Time: </strong>
              {params.row.start_time}
            </Typography>
            <Typography variant="body2">
              <strong>End Time: </strong>
              {params.row.end_time}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: 'price',
      headerName: 'Current Bid',
      flex: 1,
      renderCell: (params: any) => {
        return getFormattedPrice(params.row.current_price);
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      sortable: false,
      renderCell: (params: any) => getBidApplyCTAForAuction(params.row),
    },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <DataGrid
        rows={rows}
        columns={columns.map((col) => ({ ...col, sortable: false }))}
        pageSizeOptions={[pageSize]}
        paginationMode="server"
        paginationModel={{ page, pageSize }}
        rowCount={rowCount}
        onPaginationModelChange={(newModel: any) => setPage(newModel.page)}
        loading={loading}
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        slots={{
          loadingOverlay: DatagridLoader,
        }}
        sx={{
          // '& .MuiDataGrid-row': {
          //   maxHeight: 'inherit !important',
          //   '--height': '75px !important',
          // },
          '& .item-img-cell': {
            display: 'flex',
          },
        }}
      />
      {showAuctionDetailModal && selectedAuction && (
        <FormModal
          open={showAuctionDetailModal}
          onClose={() => {
            setShowAuctionDetailModal(false);
            setSelectedAuction(null);
          }}
          size="md"
          title="Auction Detail"
        >
          <BidWidget auction_id={parseIntoNumber(selectedAuction.id)} />
        </FormModal>
      )}
    </Box>
  );
};

export default NonEnrolledAuctionGridWidget;
